import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { uploadWithToast } from "@/lib/queuedIngest";
import { FileText, Upload, Trash2, X, FlaskConical, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ClientPicker from "@/components/ClientPicker";
import DocumentIdInput, { emptyDocSource } from "@/components/DocumentIdInput";

// SiteCentric superdomain — auto-seeded test client used for all QA-mode
// ingestion. Production CTAs ingest via the published cta-ingest API flow.
const QA_CLIENT_ID = "sitecentric";

/** Status pill mirrored from BudgetsPage. CTA backend already persists
 *  ``status: 'processing' | 'ready' | 'failed'`` — we just surface it. */
function CtaStatusBadge({ status }) {
    if (status === "processing") {
        return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-300/10 border border-amber-300/30 text-amber-200" data-testid="cta-status-processing">
                <Loader2 className="w-3 h-3 animate-spin" /> processing
            </span>
        );
    }
    if (status === "failed") {
        return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-rose-300/10 border border-rose-300/30 text-rose-200" data-testid="cta-status-failed">
                <AlertTriangle className="w-3 h-3" /> failed
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-300/10 border border-emerald-300/30 text-emerald-200" data-testid="cta-status-ready">
            <CheckCircle2 className="w-3 h-3" /> ready
        </span>
    );
}

export default function CtasPage({ qaMode = false }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);

    const load = async () => {
        try {
            const params = qaMode ? `?client_id=${encodeURIComponent(QA_CLIENT_ID)}` : "";
            const { data } = await api.get(`/admin/study-assets/ctas${params}`);
            const fresh = data.items || [];
            // Preserve in-flight optimistic placeholders whose upload
            // hasn't yet landed in the server response — see BudgetsPage.
            setItems((prev) => {
                const liveFiles = new Set(fresh.map((c) => c.filename + "|" + (c.client_id || "")));
                const stillOptimistic = prev.filter(
                    (c) => c._optimistic && !liveFiles.has(c.filename + "|" + (c.client_id || "")),
                );
                return [...stillOptimistic, ...fresh];
            });
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    useEffect(() => { setLoading(true); load(); }, [qaMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-poll every 5s while any CTA is still processing. Same pattern
    // as BudgetsPage — keeps the list live without locking the UI.
    useEffect(() => {
        const pending = items.some((c) => c.status === "processing");
        if (!pending) return;
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [items]);

    const openDetail = async (id) => {
        try {
            const { data } = await api.get(`/admin/study-assets/ctas/${id}`);
            setDetail(data);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    const remove = async (cta) => {
        if (!confirm(`Delete ${cta.filename}?`)) return;
        try {
            await api.delete(`/admin/study-assets/ctas/${cta.id}`);
            toast.success("Deleted"); load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    /** Optimistic insert — called by the modal AS the upload starts. The
     *  row sits in 'processing' until the post resolves and `load()`
     *  refreshes from the server. */
    const optimisticInsert = (placeholder) => {
        setItems((prev) => [placeholder, ...prev]);
    };

    return (
        <div className="px-8 py-8" data-testid="ctas-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED]">{qaMode ? "Quality Assurance" : "Clinical Operations"}</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">{qaMode ? "CTA Ingestion (QA)" : "CTAs"}</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        {qaMode ? (
                            <>QA Clinical Trial Agreements ingested into the SiteCentric test scope. All uploads
                            are automatically tagged <span className="font-mono text-[#c7a6ff]">client_id=sitecentric</span>.
                            Production CTA ingest runs through the published <span className="font-mono">cta-ingest</span> API flow.</>
                        ) : (
                            <>Clinical Trial Agreements ingested from Word/PDF. Stored encrypted at rest with
                            per-client envelope encryption and hash-deduped on upload.</>
                        )}
                    </p>
                </div>
                <button
                    data-testid="ctas-ingest-btn"
                    onClick={() => setOpen(true)}
                    className="px-4 py-2.5 rounded-full bg-[#7C3AED] hover:bg-[#9667ee] text-white text-sm font-medium flex items-center gap-2"
                >
                    <Upload className="w-4 h-4" /> Ingest CTA
                </button>
            </div>

            <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="ctas-list">
                {loading ? <div className="p-8 text-white/50">Loading…</div>
                    : items.length === 0 ? (
                        <div className="p-12 text-center text-white/50">
                            <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                            No CTAs yet — ingest one to get started.
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-white/[0.02] text-white/50">
                                <tr>
                                    <th className="text-left px-4 py-3 font-normal">File</th>
                                    <th className="text-left px-4 py-3 font-normal">Status</th>
                                    <th className="text-left px-4 py-3 font-normal">Client</th>
                                    <th className="text-left px-4 py-3 font-normal">Site</th>
                                    <th className="text-left px-4 py-3 font-normal">Size</th>
                                    <th className="text-left px-4 py-3 font-normal">Created</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((c) => (
                                    <tr
                                        key={c.id}
                                        data-testid={`cta-row-${c.id}`}
                                        className={`border-t border-white/5 hover:bg-white/[0.02] ${c.status === "processing" ? "" : "cursor-pointer"}`}
                                        onClick={() => c.status !== "processing" && openDetail(c.id)}
                                    >
                                        <td className="px-4 py-3 font-medium">{c.filename}</td>
                                        <td className="px-4 py-3"><CtaStatusBadge status={c.status} /></td>
                                        <td className="px-4 py-3 text-white/70">{c.client_id}</td>
                                        <td className="px-4 py-3 text-white/50">{c.site_id || "—"}</td>
                                        <td className="px-4 py-3 text-white/50">
                                            {Math.round((c.file_size_bytes || 0) / 1024)} KB
                                        </td>
                                        <td className="px-4 py-3 text-white/50">{(c.created_at || "").slice(0, 10)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); remove(c); }}
                                                className="text-white/40 hover:text-rose-400"
                                                data-testid={`cta-delete-${c.id}`}
                                                disabled={c.status === "processing"}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </div>

            {open && (
                <CtaIngestModal
                    qaMode={qaMode}
                    onClose={() => setOpen(false)}
                    onUploadStart={optimisticInsert}
                    onUploadDone={load}
                />
            )}
            {detail && <CtaDetailModal cta={detail} onClose={() => setDetail(null)} />}
        </div>
    );
}

function CtaIngestModal({ qaMode = false, onClose, onUploadStart, onUploadDone }) {
    const [source, setSource] = useState(emptyDocSource("file"));
    const [clientId, setClientId] = useState(qaMode ? QA_CLIENT_ID : "");
    const [siteId, setSiteId] = useState("");
    // When the same file (by SHA-256) was already ingested, the backend
    // returns the existing CTA + a `deduplicated=true` flag. Tick this
    // box to instead overwrite & re-run the AI extraction — useful when
    // the extraction prompt changed or the previous run failed.
    const [forceReextract, setForceReextract] = useState(false);

    /** Fire-and-forget submit — closes the modal immediately so the user
     *  can navigate elsewhere while the upload finishes in the
     *  background. Optimistically inserts a row in 'processing' state
     *  so they see their file in the list right away.
     *
     *  Errors after the modal is gone are surfaced via toast. */
    const submit = () => {
        const cid = qaMode ? QA_CLIENT_ID : clientId.trim();
        const fromS3 = source.mode === "document_id";
        const file = source.file;
        const docId = (source.documentId || "").trim();
        if (!cid) { toast.error("client_id required"); return; }
        if (!fromS3 && !file) { toast.error("File required"); return; }
        if (fromS3 && !docId) { toast.error("document_id required"); return; }

        const label = fromS3 ? docId : file.name;
        const sizeBytes = fromS3 ? 0 : file.size;
        const tempId = `temp-${Date.now()}`;
        const placeholder = {
            id: tempId,
            filename: label,
            client_id: cid,
            site_id: siteId.trim() || null,
            file_size_bytes: sizeBytes,
            created_at: new Date().toISOString(),
            status: "processing",
            _optimistic: true,
        };
        onUploadStart?.(placeholder);

        const fd = new FormData();
        if (fromS3) fd.append("document_id", docId);
        else        fd.append("file", file);
        fd.append("client_id", cid);
        if (siteId.trim()) fd.append("site_id", siteId.trim());
        if (forceReextract) fd.append("force_reextract", "true");

        // Close immediately — the queued helper drives the toast lifecycle.
        onClose();

        uploadWithToast(api, `/admin/study-assets/ctas/ingest`, fd, {
            filename: label,
            queuedLabel: forceReextract ? `Re-extracting ${label}…` : `Uploading ${label}…`,
            onDone: () => onUploadDone?.(),
        }).catch(() => {
            // uploadWithToast already surfaced the toast — just refresh
            // the list so the optimistic placeholder is dropped.
            onUploadDone?.();
        });
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div
                className="bg-[#0c0c10] border border-white/10 rounded-2xl w-full max-w-lg p-6"
                onMouseDown={(e) => e.stopPropagation()}
                data-testid="cta-ingest-modal"
            >
                <div className="flex items-start justify-between mb-4">
                    <h2 className="font-display text-2xl">Ingest CTA</h2>
                    <button onClick={onClose}><X className="w-5 h-5 text-white/50" /></button>
                </div>
                <div className="space-y-3">
                    <DocumentIdInput
                        value={source}
                        onChange={setSource}
                        accept=".doc,.docx,.pdf,.txt"
                        testId="cta"
                        label="CTA document"
                    />
                    {qaMode ? (
                        <div className="p-2.5 rounded-md border border-[#7C3AED]/30 bg-[#7C3AED]/5 text-xs text-white/70 flex items-center gap-2" data-testid="cta-qa-banner">
                            <FlaskConical className="w-3.5 h-3.5 text-[#c7a6ff]" />
                            Locked to <code className="text-[#c7a6ff] mx-1">client_id=sitecentric</code> (test scope).
                        </div>
                    ) : (
                        <ClientPicker
                            value={clientId} onChange={setClientId}
                            label="Client" required excludeDisabled
                            testId="cta-client-id"
                        />
                    )}
                    <input
                        placeholder="site_id (optional)" value={siteId}
                        data-testid="cta-site-id"
                        onChange={(e) => setSiteId(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                    />
                    <label className="flex items-start gap-2 p-2.5 rounded-md border border-amber-400/20 bg-amber-400/5 cursor-pointer" data-testid="cta-force-reextract-wrap">
                        <input
                            type="checkbox"
                            checked={forceReextract}
                            onChange={(e) => setForceReextract(e.target.checked)}
                            data-testid="cta-force-reextract"
                            className="mt-0.5 accent-amber-400"
                        />
                        <span className="text-xs text-white/70 leading-relaxed">
                            <span className="text-amber-300 font-medium">Re-extract if already ingested.</span>
                            <span className="block text-white/40 mt-0.5">
                                Bypasses SHA-256 dedup and re-runs the AI extraction
                                on top of the existing record. Use after a prompt change
                                or to recover from a failed extraction.
                            </span>
                        </span>
                    </label>
                </div>
                <p className="text-xs text-white/40 mt-2">
                    💡 This UI is for testing. In production, CTA ingestion happens via the
                    <code className="px-1 bg-white/5 rounded mx-1">cta-ingest</code> published flow.
                </p>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-white/60">Cancel</button>
                    <button
                        onClick={submit}
                        data-testid="cta-submit-btn"
                        className="px-4 py-2 rounded-full bg-[#7C3AED] text-white text-sm"
                    >
                        Ingest
                    </button>
                </div>
                <p className="text-xs text-white/40 mt-3">
                    Supports .doc / .docx / .pdf / .txt. Extraction continues in the background after upload — you can close this dialog and keep working.
                </p>
            </div>
        </div>
    );
}

function CtaDetailModal({ cta, onClose }) {
    const payload = cta.payload || {};
    const clauses = payload.clauses || {};
    const [tab, setTab] = useState("clauses");
    const [analysis, setAnalysis] = useState({});
    const [busy, setBusy] = useState(null);

    const runAnalysis = async (kind, opts = {}) => {
        const busyKey = opts.force ? `${kind}-force` : kind;
        setBusy(busyKey);
        try {
            let data;
            if (kind === "evaluate") {
                const url = `/admin/study-analysis/ctas/${cta.id}/evaluate${opts.force ? "?force_refresh=true" : ""}`;
                const r = await api.post(url);
                data = r.data;
                // The evaluate route is now async — if status='processing'
                // we poll the status endpoint until it's ready/failed. The
                // K8s ingress timeout is 60s, but the actual LLM call can
                // take 30-180s with provider fallbacks, so polling is the
                // ONLY reliable contract.
                if (data?.status === "processing") {
                    const startedAt = Date.now();
                    const maxMs = 5 * 60 * 1000; // 5 min ceiling
                    let polled;
                    while (Date.now() - startedAt < maxMs) {
                        await new Promise((res) => setTimeout(res, 3000));
                        const sr = await api.get(
                            `/admin/study-analysis/ctas/${cta.id}/evaluate-status`,
                        );
                        polled = sr.data;
                        if (polled?.status === "ready") { data = polled; break; }
                        if (polled?.status === "failed") {
                            throw new Error(polled?.error || "AI evaluation failed");
                        }
                    }
                    if (!polled || polled.status === "processing") {
                        throw new Error(
                            "AI evaluation is taking longer than expected. " +
                            "It may still complete — check back in a minute.",
                        );
                    }
                }
            } else if (kind === "compare") {
                const r = await api.post(`/admin/study-analysis/ctas/${cta.id}/compare-to-history`); data = r.data;
            } else if (kind === "feasibility") {
                const r = await api.post(`/admin/study-analysis/ctas/${cta.id}/feasibility`); data = r.data;
            }
            setAnalysis((a) => ({ ...a, [kind]: data }));
            setTab(kind === "evaluate" ? "evaluate" : kind);
            toast.success(
                opts.force ? `${kind} re-run complete (fresh LLM call)` : `${kind} analysis complete`,
            );
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message;
            toast.error(formatApiErrorDetail(msg) || "Analysis failed");
        }
        finally { setBusy(null); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div
                className="bg-[#0c0c10] border border-white/10 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-auto p-6"
                onMouseDown={(e) => e.stopPropagation()}
                data-testid="cta-detail-modal"
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h2 className="font-display text-2xl">{cta.filename}</h2>
                        <p className="text-xs text-white/40 mt-1">
                            {cta.client_id} · {(cta.file_size_bytes / 1024).toFixed(0)} KB · hash {(cta.source_signature || "").slice(0, 12)}
                        </p>
                    </div>
                    <button onClick={onClose}><X className="w-5 h-5 text-white/50" /></button>
                </div>

                <div className="flex gap-2 mb-4 flex-wrap">
                    <button
                        onClick={() => runAnalysis("evaluate")} disabled={busy}
                        data-testid="cta-evaluate-btn"
                        className="px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-300 text-xs hover:bg-amber-400/20 disabled:opacity-50"
                    >{busy === "evaluate" ? "…" : "Evaluate"}</button>
                    <button
                        onClick={() => runAnalysis("evaluate", { force: true })} disabled={busy}
                        data-testid="cta-reevaluate-btn"
                        title="Bypass the datalake cache and run a fresh LLM evaluation (use this after a prompt or model change)."
                        className="px-3 py-1.5 rounded-full bg-amber-400/5 border border-amber-400/20 text-amber-200/80 text-xs hover:bg-amber-400/15 disabled:opacity-50"
                    >{busy === "evaluate-force" ? "…" : "Re-evaluate"}</button>
                    <button
                        onClick={() => runAnalysis("compare")} disabled={busy}
                        data-testid="cta-compare-btn"
                        className="px-3 py-1.5 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 text-xs hover:bg-cyan-400/20 disabled:opacity-50"
                    >{busy === "compare" ? "…" : "Compare to history"}</button>
                    <button
                        onClick={() => runAnalysis("feasibility")} disabled={busy}
                        data-testid="cta-feasibility-btn"
                        className="px-3 py-1.5 rounded-full bg-violet-400/10 border border-violet-400/30 text-violet-300 text-xs hover:bg-violet-400/20 disabled:opacity-50"
                    >{busy === "feasibility" ? "…" : "Feasibility"}</button>
                </div>

                <div className="flex gap-2 border-b border-white/10 mb-3">
                    {[
                        ["clauses", "Clauses"],
                        ["metadata", "Metadata"],
                        ["payment", "Payment Terms"],
                        ["evaluate", `AI Review${analysis.evaluate ? ` (${(analysis.evaluate.executive_summary?.overall_risk || "?").toUpperCase()})` : ""}`],
                        ["compare", `History${analysis.compare ? ` (${analysis.compare.deviations?.length || 0})` : ""}`],
                        ["feasibility", `Feasibility${analysis.feasibility ? ` (${analysis.feasibility.recommendations?.length || 0})` : ""}`],
                    ].map(([k, l]) => (
                        <button key={k} onClick={() => setTab(k)}
                            data-testid={`cta-tab-${k}`}
                            className={`px-3 py-2 text-sm ${tab === k ? "text-white border-b-2 border-[#7C3AED]" : "text-white/40 hover:text-white/70"}`}
                        >{l}</button>
                    ))}
                </div>

                {tab === "metadata" && (
                    <pre className="text-xs whitespace-pre-wrap text-white/70">{JSON.stringify(payload.metadata || {}, null, 2)}</pre>
                )}

                {tab === "payment" && (
                    <div>
                        <pre className="text-xs whitespace-pre-wrap text-white/70 mb-3">{JSON.stringify(payload.payment_terms || {}, null, 2)}</pre>
                        <h4 className="text-xs uppercase tracking-wider text-white/40 mb-2">Milestones</h4>
                        {(payload.milestones || []).length === 0 ? <span className="text-white/40 text-xs">No milestones extracted.</span>
                            : <ul className="text-sm space-y-1">
                                {payload.milestones.map((m, i) => (
                                    <li key={i} className="flex items-center gap-3">
                                        <span className="text-white/80">{m.name}</span>
                                        <span className="text-emerald-400">${m.amount_usd}</span>
                                        <span className="text-white/40 text-xs">{m.trigger}</span>
                                    </li>
                                ))}
                            </ul>}
                    </div>
                )}

                {tab === "clauses" && (
                    Object.keys(clauses).length === 0 ? <span className="text-white/40 text-xs">No clauses extracted.</span>
                        : <div className="space-y-3">
                            {Object.entries(clauses).map(([k, v]) => v && (
                                <div key={k} className="border border-white/10 rounded-lg p-3 bg-white/[0.02]">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium capitalize">{k.replace(/_/g, " ")}</span>
                                        {v.section && <span className="text-xs text-white/40">§ {v.section}</span>}
                                    </div>
                                    {v.summary && <p className="text-xs text-white/60 mt-1">{v.summary}</p>}
                                    {v.text && <details className="mt-2"><summary className="text-xs text-white/40 cursor-pointer">verbatim</summary>
                                        <pre className="text-xs text-white/50 whitespace-pre-wrap mt-1">{v.text}</pre>
                                    </details>}
                                    {v.issues_for_site?.length > 0 && (
                                        <div className="mt-2 text-xs text-amber-400">
                                            ⚠ {v.issues_for_site.join("; ")}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                )}

                {tab === "evaluate" && <FindingsList result={analysis.evaluate} />}
                {tab === "compare" && <DeviationsList result={analysis.compare} />}
                {tab === "feasibility" && <RecommendationsList result={analysis.feasibility} />}
            </div>
        </div>
    );
}

function FindingsList({ result }) {
    if (!result) return <div className="text-white/40 text-sm py-4">Click "Evaluate" to run the CTA AI Agent.</div>;
    const es = result.executive_summary || {};
    const clauses = result.clause_assessment || [];
    const critical = result.critical_issues || [];
    const negotiations = result.recommended_negotiations || [];
    const realism = result.business_realism || {};
    const md = result.metadata || {};
    const counts = result.summary_counts || {};

    const riskColor = {
        low: "text-emerald-300 bg-emerald-300/10 border-emerald-300/30",
        moderate: "text-sky-300 bg-sky-300/10 border-sky-300/30",
        elevated: "text-amber-300 bg-amber-300/10 border-amber-300/30",
        high: "text-rose-300 bg-rose-300/10 border-rose-300/30",
    };
    const clauseRiskColor = {
        standard: "border-emerald-400/20 bg-emerald-400/[0.03]",
        needs_clarification: "border-sky-400/20 bg-sky-400/[0.03]",
        concerning: "border-amber-400/30 bg-amber-400/5",
        high_risk: "border-rose-400/30 bg-rose-400/5",
    };
    const actionLabel = {
        accept: "accept",
        negotiate: "negotiate",
        redline: "redline",
        reject: "reject",
    };

    return (
        <div data-testid="cta-ai-review">
            {md._llm_unavailable_reason && (
                <div className="mb-3 p-3 rounded-md border border-amber-400/30 bg-amber-400/5 text-xs text-amber-200" data-testid="ai-budget-warning">
                    LLM unavailable: {md._llm_unavailable_reason}
                </div>
            )}
            {md._extractor === "deterministic_fallback" && (
                <div className="mb-3 p-3 rounded-md border border-sky-400/30 bg-sky-400/5 text-xs text-sky-200" data-testid="ai-fallback-warning">
                    AI evaluation unavailable — showing deterministic rules-engine output. Re-run after LLM gateway recovers.
                </div>
            )}

            {/* Executive Summary */}
            <div className={`mb-4 p-4 rounded-md border ${riskColor[es.overall_risk] || riskColor.moderate}`} data-testid="cta-executive-summary">
                <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs uppercase tracking-wider opacity-70">Overall Risk</span>
                    <span className="font-display text-2xl tracking-tight">{(es.overall_risk || "?").toUpperCase()}</span>
                </div>
                <p className="text-sm">{es.fairness_assessment}</p>
                {(es.key_negotiation_priorities || []).length > 0 && (
                    <div className="mt-3">
                        <div className="text-[11px] uppercase tracking-wider opacity-60 mb-1.5">Key Negotiation Priorities</div>
                        <ul className="text-xs space-y-1">
                            {es.key_negotiation_priorities.map((p, i) => (
                                <li key={i}>• {p}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Counts strip */}
            <div className="grid grid-cols-5 gap-2 mb-4 text-xs">
                <Pill label="High risk" n={counts.high_risk || 0} color="rose" />
                <Pill label="Concerning" n={counts.concerning || 0} color="amber" />
                <Pill label="Clarify" n={counts.needs_clarification || 0} color="white" />
                <Pill label="Standard" n={counts.standard || 0} color="emerald" />
                <Pill label="Critical" n={critical.length} color="rose" />
            </div>

            {/* Critical issues */}
            {critical.length > 0 && (
                <Section title="Critical Issues">
                    <div className="space-y-2" data-testid="cta-critical-issues">
                        {critical.map((c, i) => (
                            <div key={i} className={`border rounded-lg p-3 text-sm ${
                                c.severity === "high" ? "border-rose-400/40 bg-rose-400/5" : "border-amber-400/30 bg-amber-400/5"
                            }`} data-testid={`cta-critical-${i}`}>
                                <div className="flex items-center justify-between">
                                    <span className="font-medium">{c.title}</span>
                                    <span className="text-xs uppercase opacity-60">{c.severity}</span>
                                </div>
                                {c.clause_section && <div className="text-xs text-white/45 mt-0.5">{c.clause_section}</div>}
                                <p className="text-xs text-white/65 mt-1.5">{c.explanation}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Clause assessment table */}
            <Section title="Clause-by-Clause Assessment">
                <div className="overflow-hidden rounded-lg border border-white/10" data-testid="cta-clause-table">
                    <table className="w-full text-xs">
                        <thead className="bg-white/[0.03] text-white/45">
                            <tr>
                                <th className="px-3 py-2 text-left font-medium">Section</th>
                                <th className="px-3 py-2 text-left font-medium">Risk</th>
                                <th className="px-3 py-2 text-left font-medium">Action</th>
                                <th className="px-3 py-2 text-left font-medium">Explanation</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clauses.map((c, i) => (
                                <tr key={i} className={`border-t border-white/5 ${clauseRiskColor[c.risk_level] || ""}`} data-testid={`cta-clause-${i}`}>
                                    <td className="px-3 py-2 font-medium align-top whitespace-nowrap">{c.section}</td>
                                    <td className="px-3 py-2 align-top">
                                        <span className="font-mono text-[10px] uppercase">{(c.risk_level || "").replace(/_/g, " ")}</span>
                                        {c.industry_standard && <div className="text-[10px] text-white/40">industry-standard</div>}
                                    </td>
                                    <td className="px-3 py-2 align-top text-white/70">{actionLabel[c.recommended_action] || c.recommended_action}</td>
                                    <td className="px-3 py-2 align-top text-white/70">{c.explanation}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* Recommended negotiations */}
            {negotiations.length > 0 && (
                <Section title="Recommended Negotiations">
                    <div className="space-y-2" data-testid="cta-negotiations">
                        {negotiations.map((n, i) => (
                            <div key={i} className="border border-white/10 rounded-lg p-3 text-sm bg-white/[0.02]" data-testid={`cta-negotiation-${i}`}>
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] uppercase tracking-wider text-[#c7a6ff]">{(n.priority_area || "").replace(/_/g, " ")}</span>
                                    <span className="text-[10px] uppercase opacity-60">sponsor likely: {n.likelihood_sponsor_accepts}</span>
                                </div>
                                {n.current_language && (
                                    <div className="mt-2">
                                        <div className="text-[10px] uppercase text-white/40 mb-0.5">Current</div>
                                        <div className="text-xs text-white/55 italic">"{n.current_language}"</div>
                                    </div>
                                )}
                                <div className="mt-2">
                                    <div className="text-[10px] uppercase text-emerald-300/70 mb-0.5">Proposed</div>
                                    <div className="text-xs text-emerald-200">{n.proposed_change}</div>
                                </div>
                                {n.rationale && <p className="text-xs text-white/55 mt-2">{n.rationale}</p>}
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Business realism */}
            <Section title="Business Realism">
                <div className="border border-white/10 rounded-lg p-3 bg-white/[0.02] text-sm" data-testid="cta-business-realism">
                    <div className="flex items-center gap-4 mb-2 text-xs">
                        <span><span className="text-white/45">Sponsor likely accepts:</span> <span className="text-white">{realism.sponsor_acceptance_likelihood}</span></span>
                        <span><span className="text-white/45">Commercially reasonable:</span> <span className="text-white">{realism.commercially_reasonable ? "yes" : "no"}</span></span>
                        <span><span className="text-white/45">Market-standard:</span> <span className="text-white">{realism.market_standard ? "yes" : "no"}</span></span>
                    </div>
                    <p className="text-white/70">{realism.narrative}</p>
                </div>
            </Section>
        </div>
    );
}

function DeviationsList({ result }) {
    if (!result) return <div className="text-white/40 text-sm py-4">Click "Compare to history" to run.</div>;
    const ds = result.deviations || [];
    return (
        <div data-testid="cta-deviations-list">
            <div className="text-xs text-white/50 mb-3">
                Compared to {result.history_sample_size} prior CTA(s) for this client.
            </div>
            {ds.length === 0 ? <div className="text-white/40 text-sm">No material deviations.</div>
                : <div className="space-y-2">
                    {ds.map((d, i) => (
                        <div key={i} className={`border rounded-lg p-3 text-sm ${d.severity === "warning" ? "border-amber-400/30 bg-amber-400/5" : "border-white/10 bg-white/[0.02]"}`}>
                            <div className="flex items-center justify-between">
                                <span className="font-medium">{d.field}</span>
                                <span className="text-xs uppercase opacity-60">{d.severity}</span>
                            </div>
                            <p className="text-xs text-white/60 mt-1">{d.rationale}</p>
                        </div>
                    ))}
                </div>}
        </div>
    );
}

function RecommendationsList({ result }) {
    if (!result) return <div className="text-white/40 text-sm py-4">Click "Feasibility" to run.</div>;
    const recs = result.recommendations || [];
    return (
        <div data-testid="cta-feasibility-list">
            <div className="text-xs text-white/50 mb-3">
                {recs.length} recommendation(s) · history n={result.history_summary?.sample_size || 0}
            </div>
            <div className="space-y-2">
                {recs.map((r, i) => (
                    <div key={i} className={`border rounded-lg p-3 text-sm ${
                        r.severity === "critical" ? "border-rose-400/30 bg-rose-400/5"
                            : r.severity === "warning" ? "border-amber-400/30 bg-amber-400/5"
                                : "border-white/10 bg-white/[0.02]"
                    }`}>
                        <div className="flex items-center justify-between">
                            <span className="font-medium">{r.label}</span>
                            <span className="text-xs uppercase opacity-60">{r.severity} · {r.source}</span>
                        </div>
                        <p className="text-xs text-white/60 mt-1">{r.rationale}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Pill({ label, n, color }) {
    const colors = { rose: "text-rose-400", amber: "text-amber-400", emerald: "text-emerald-400", white: "text-white" };
    return (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
            <div className={`text-base font-medium tabular-nums ${colors[color]}`}>{n}</div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mb-5">
            <div className="text-xs uppercase tracking-wider text-white/40 mb-2">{title}</div>
            {children}
        </div>
    );
}
